import React, { useEffect, useState, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router";
import { useLocation } from "react-router-dom";
import { useQueryClient } from '@tanstack/react-query';
// @YUp
import * as Yup from "yup";
import { Button, Grid, TextField, FormControl, MenuItem, Select, FormHelperText, IconButton } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import API from "../../utils/Axios.utils";
import { Notification } from "../../utils/Notification";
import FIelds from "../../uicomponents/FIelds";
import TextArea from "../../uicomponents/TextArea";
import CustomSelect from "../../uicomponents/CustomSelect";
import CustomMultiSelect from "../../uicomponents/CustomMultiSelect";
import TextEditor from "../../utils/TextEditor";
import useAuth from "../../hooks/useAuth";

// Statuses that require a Notes entry
const NOTES_REQUIRED_STATUSES = ['Hold', 'Cancelled', 'Close-No_Client_Response', 'Close-Unable_To_Fulfill'];

// Style constants to prevent recreation on every render
const containerStyle = { marginTop: 20 };
const formContainerStyle = {
  border: "1px solid #989898",
  borderRadius: "10px",
  padding: "15px",
};
const headerStyle = {
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  fontSize: "20px",
  fontWeight: "500",
  letterSpacing: 1,
  textAlign: "left",
};
const gridContainerStyle = { display: "grid" };
const labelStyle = { fontSize: "16px" };
const requiredStyle = { color: 'red' };

const EditJob = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isFormSubmitted, setIsFormSubmitted] = React.useState(false);

  // Snackbar
  const [alert, setAlert] = React.useState(false);
  const [alertContent, setAlertContent] = React.useState("");
  const [alertType, setAlertType] = React.useState("");

  // AI Job Description Generator States
  const [aiKeywords, setAiKeywords] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiGeneratedContent, setAiGeneratedContent] = useState("");

  const location = useLocation();
  const jobDetails = location.state?.jobDetails;

  // Additive resume-scoring instructions — appended after the RAG service's own
  // base scoring prompt, never a replacement for it. Seeded from the job's
  // already-linked prompt (if any); leaving it blank scores as usual.
  const [scoringPromptText, setScoringPromptText] = useState(jobDetails?.scoringPrompt?.prompt_text || "");
  // Recruiter-chosen label for the saved prompt itself — separate from the job
  // title, so two jobs sharing the same title (different clients/requirements)
  // still show up distinctly in the reuse dropdowns.
  const [promptName, setPromptName] = useState(jobDetails?.scoringPrompt?.prompt_name || "");
  // The name this prompt currently has in the DB, as of the last time it was
  // loaded/picked — compared against promptName on submit to tell a pure
  // rename (safe to update in place) apart from picking a fresh one.
  const [originalPromptName, setOriginalPromptName] = useState(jobDetails?.scoringPrompt?.prompt_name || "");
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [selectedSavedPromptId, setSelectedSavedPromptId] = useState(jobDetails?.scoring_prompt_id || "");
  console.log('jobDetails :- ', jobDetails)
  const handleAlert = () => {
    setAlert(false);
  };

  const [editorContent, setEditorContent] = React.useState(
    jobDetails.job_description
  );
  const [clearData, setClearData] = React.useState(false);

  // For Validation
  const [editorIsEmpty, setEditorIsEmpty] = React.useState(false);

  // Skill Data
  const [skillData, setSkillData] = useState([]);

  const handleEditorEmptyChange = (isEmpty) => {
    setEditorIsEmpty(isEmpty);
  };
  // validation code end

  const handleContentChange = (content) => {
    // Handle the editor content here (e.g., store it in state)
    setEditorContent(content);
  };

  const handleGenerateAIDescription = async (values, setFieldValue) => {
    if (!values.title) {
      setAlertContent("Please enter a Job Title first to generate a description.");
      setAlertType("error");
      setAlert(true);
      return;
    }

    setIsGenerating(true);
    try {
      const payload = {
        title: values.title,
        keywords: aiKeywords || (values.techSkills && values.techSkills.length > 0 ? values.techSkills.join(', ') : '') || values.title
      };

      const res = await API.post("/api/job/generate-description", payload, config);
      const generatedHtml = res?.data?.description;

      if (generatedHtml) {
        setAiGeneratedContent(generatedHtml);
        setEditorContent(generatedHtml); // Update Formik/editor content state
        setFieldValue("job_description", generatedHtml);
        setAlertContent("AI Job Description generated successfully!");
        setAlertType("success");
        setAlert(true);
      } else {
        throw new Error("Invalid response received from server.");
      }
    } catch (error) {
      console.error("AI Generation Error:", error);
      setAlertContent(error?.response?.data?.message || error?.message || "Failed to generate job description.");
      setAlertType("error");
      setAlert(true);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePrefillKeywords = (values) => {
    const titleVal = values.title || "";
    const techSkillsVal = (values.techSkills && values.techSkills.length > 0)
      ? values.techSkills.join(", ")
      : "";
    const combined = [titleVal, techSkillsVal].filter(Boolean).join(", ");
    setAiKeywords(combined);
  };

  const handleGenerateScoringPrompt = async (values) => {
    if (!values.title || !editorContent || !editorContent.replace(/<[^>]*>/g, '').trim()) {
      setAlertContent("Please fill in Job Title and Job Description first.");
      setAlertType("error");
      setAlert(true);
      return;
    }

    setIsGeneratingPrompt(true);
    try {
      const payload = {
        title: values.title,
        job_description: editorContent,
        techSkills: values.techSkills,
      };
      const res = await API.post("/api/job/generate-scoring-prompt", payload, config);
      setScoringPromptText(res?.data?.prompt_text || "");
      setSelectedSavedPromptId("");
      // Default the name to the job title, same convenience default as the rest
      // of this feature — recruiter can still rename before saving.
      if (!promptName.trim()) {
        setPromptName(values.title);
      }
    } catch (error) {
      console.error("Scoring prompt generation error:", error);
      setAlertContent(error?.response?.data?.message || "Failed to generate scoring instructions.");
      setAlertType("error");
      setAlert(true);
    } finally {
      setIsGeneratingPrompt(false);
    }
  };

  // Shared by both reuse dropdowns (by Prompt Name and by Job Title) — they
  // list the same saved prompts, just labeled differently, and both drive this
  // same selectedSavedPromptId state, which is what keeps them in sync: picking
  // an entry in either one highlights the matching entry in the other too.
  const handleSelectSavedPrompt = (id, form) => {
    setSelectedSavedPromptId(id);
    if (!id) {
      return;
    }
    const picked = savedPrompts.find((p) => String(p.scoring_prompt_id) === String(id));
    const pickedName = picked?.prompt_name || picked?.title || "";
    setScoringPromptText(picked?.prompt_text || "");
    setPromptName(pickedName);
    setOriginalPromptName(pickedName);
    form.setFieldValue("scoring_prompt_id", id);
  };

  const handleScoringPromptTextChange = (value, form) => {
    setScoringPromptText(value);
    // Editing the actual instructions means this is now new/edited content,
    // not that exact saved entry — clear the link so submit forks a fresh row.
    // (Unlike a pure rename, this could change what the prompt means, so it
    // must never silently apply to other jobs still reusing the original row.)
    if (selectedSavedPromptId) {
      setSelectedSavedPromptId("");
      form.setFieldValue("scoring_prompt_id", "");
    }
  };

  const handlePromptNameChange = (value, form) => {
    // Renaming does NOT fork — it never changes the actual scoring
    // instructions, so it's safe to update the existing row's label in place
    // on submit (see originalPromptName comparison there). The link
    // (selectedSavedPromptId / scoring_prompt_id) stays intact.
    setPromptName(value);
  };

  const initialValues = {
    title: jobDetails.title,
    // location: jobDetails.location,
    location: (() => {
      try {
        const raw = jobDetails?.location;
        if (!raw) return '';

        // Already array
        if (Array.isArray(raw)) {
          return raw.join(', ');
        }

        if (typeof raw === 'string') {
          let trimmed = raw.trim();

          // Case A: double‑encoded array → "\"[\"Pune\",\"Mumbai\"]\""
          // First parse outer string, then inner array
          if (trimmed.startsWith('"') && trimmed.endsWith('"') && trimmed.includes('[')) {
            const inner = JSON.parse(trimmed);          // → "[\"Pune\",\"Mumbai\"]"
            const arr = JSON.parse(inner);             // → ["Pune","Mumbai"]
            return arr.join(', ');
          }

          // Case B: JSON array string ["Pune","Mumbai"]
          if (trimmed.startsWith('[')) {
            return JSON.parse(trimmed).join(', ');
          }

          // Case C: escaped single string "\"Pune\""
          if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
            return JSON.parse(trimmed);                // → "Pune"
          }

          // Case D: plain string "Pune"
          return trimmed;
        }

        return '';
      } catch (e) {
        console.log('initialValues.location parse error:', jobDetails?.location, e);
        return '';
      }
    })(),
    experience: jobDetails.experience,
    currency: jobDetails.currency,
    budget: jobDetails.budget,
    budget_uom: jobDetails.budget_uom,
    mode_of_work: jobDetails.mode_of_work,
    notice_period: jobDetails.notice_period,
    // primary_skills: jobDetails.primary_skills,
    job_description: jobDetails.job_description,
    status: jobDetails.job_status,
    notes: jobDetails.notes,
    coreSkillId: jobDetails?.coreSkillId,      // NEW
    techSkills: jobDetails?.techSkills,
    client_id: jobDetails.client_id || '',
    contact_id: jobDetails?.contact_person?.contact_id || '',
    no_of_requirements: jobDetails.no_of_requirements || 1,
    contact_person_name: jobDetails.contact_person_name || '',
    contact_person_number: jobDetails.contact_person_number || '',
    contact_person_email: jobDetails.contact_person_email || '',
    fulfilled_count: jobDetails.fulfilled_count || 0,
    background_verification: jobDetails.background_verification === 'Yes' ? true : false,
    assignment_duration_days: jobDetails.assignment_duration_days || 90,
    needs_to_review: jobDetails.needs_to_review || false,
    pipeline_type: jobDetails.pipeline_type || '',
    voice_bot_id: jobDetails.voice_bot_id || '',
    db_customer_name: jobDetails.db_customer_name || '',
    campaign_name: jobDetails.campaign_name || '',
    organisation_name: jobDetails.organisation_name || '',
    customer_name: jobDetails.customer_name || '',
    processes: jobDetails.processes || '',
    module_name: jobDetails.module_name || '',
    scoring_prompt_id: jobDetails.scoring_prompt_id || '',
  };

  const { token, role, isRecruiter } = useAuth();

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  // const onSubmit = async (values) => {
  //   if (editorIsEmpty) {
  //     setIsFormSubmitted(true);
  //   } else {
  //     values.job_description = editorContent;
  //     try {
  //       const res = await API.put(
  //         `/api/job/${jobDetails.job_id}`,
  //         values,
  //         config
  //       );
  //       setAlertContent(res?.data?.message);
  //       setAlertType("success");
  //       setAlert(true);
  //       setTimeout(() => {
  //         navigate("/jobs");
  //       }, 1500);
  //     } catch (error) {
  //       setAlertContent(error?.response?.data?.message);
  //       setAlertType("error");
  //       setAlert(true);
  //     }
  //   }
  // };

  // const onSubmit = async (values) => {
  //   if (editorIsEmpty) {
  //     setIsFormSubmitted(true);
  //     return;
  //   }

  //   values.job_description = editorContent;
  //   const parts = values.location
  //     ? values.location.split(',').map(loc => loc.trim()).filter(loc => loc)
  //     : [];

  //   let finalLocation;
  //   if (parts.length === 1) {
  //     finalLocation = parts[0];                  // "Pune"
  //   } else if (parts.length > 1) {
  //     finalLocation = JSON.stringify(parts);     // ["Pune","Mumbai"]
  //   } else {
  //     finalLocation = '';
  //   }

  //   const payload = {
  //     ...values,
  //     location: finalLocation,
  //     background_verification: values.background_verification ? 'Yes' : 'No',
  //   };

  //   try {
  //     const res = await API.put(`/api/job/${jobDetails.job_id}`, payload, config);

  //     const { message, showAlert, alertType, fulfillment } = res?.data || {};

  //     // Build enhanced message for full-filled jobs
  //     let finalMessage = message || "Job updated successfully.";
  //     if (showAlert && alertType === "FULL_FILLED" && fulfillment) {
  //       finalMessage = `${message} All ${fulfillment.total} positions are now filled! 🎉`;
  //     }

  //     setAlertContent(finalMessage);
  //     setAlertType("success");
  //     setAlert(true);

  //     setTimeout(() => {
  //       navigate("/jobs");
  //     }, 2000); // Slightly longer for full-filled message
  //   } catch (error) {
  //     setAlertContent(error?.response?.data?.message || "Update failed");
  //     setAlertType("error");
  //     setAlert(true);
  //   }
  // };

  const onSubmit = async (values) => {
    if (editorIsEmpty) {
      setIsFormSubmitted(true);
      return;
    }

    values.job_description = editorContent;
    const parts = values.location
      ? values.location.split(',').map(loc => loc.trim()).filter(loc => loc)
      : [];

    let finalLocation = [];
    if (parts.length > 0) {
      finalLocation = parts;
    }

    const payload = {
      ...values,
      location: finalLocation.length > 0 ? finalLocation : null,
      background_verification: values.background_verification ? 'Yes' : 'No',
      job_description: editorContent,
    };

    // Additive resume-scoring instructions: save new/edited text as its own
    // reusable row first, then link the job to it. A saved prompt picked as-is
    // (or the job's already-linked prompt, left untouched) already carries its
    // id; clearing the box unlinks the job back to default scoring.
    if (scoringPromptText.trim() && !payload.scoring_prompt_id) {
      try {
        const promptRes = await API.post(
          "/api/job/scoring-prompts",
          {
            title: values.title,
            prompt_name: promptName.trim() || values.title,
            prompt_text: scoringPromptText.trim(),
            source: selectedSavedPromptId ? "reused" : "generated",
            job_id: jobDetails.job_id,
            job_code: jobDetails.job_code,
          },
          config
        );
        payload.scoring_prompt_id = promptRes?.data?.scoring_prompt_id;
      } catch (promptError) {
        console.error("Failed to save scoring instructions:", promptError);
        delete payload.scoring_prompt_id;
      }
    } else if (
      payload.scoring_prompt_id &&
      promptName.trim() &&
      promptName.trim() !== originalPromptName
    ) {
      // Link intact, instructions untouched, only the name changed — update
      // the existing row in place instead of forking a new one.
      try {
        await API.put(
          `/api/job/scoring-prompts/${payload.scoring_prompt_id}`,
          { prompt_name: promptName.trim() },
          config
        );
      } catch (renameError) {
        console.error("Failed to rename scoring prompt:", renameError);
        // Non-blocking — the job still saves and links correctly either way.
      }
    }
    if (!scoringPromptText.trim()) {
      payload.scoring_prompt_id = null; // box cleared → unlink, revert to default scoring
    }

    try {
      const res = await API.put(`/api/job/${jobDetails.job_id}`, payload, config);
      const data = res?.data || {};

      // ✅ Handle detailed error responses (just error field)
      if (data.error) {
        setAlertContent(data.error);
        setAlertType("error");
        setAlert(true);
        return;
      }

      // ✅ Success with enhanced messaging
      let finalMessage = data.message || "Job updated successfully.";
      const { showAlert, alertType, fulfillment, status_priority, auto_updated } = data;

      // 🎉 Full-filled celebration
      if (showAlert && alertType === "FULL_FILLED" && fulfillment) {
        finalMessage = `${finalMessage} All ${fulfillment.total} positions filled! 🎉`;
      }

      // 📋 Status info
      // if (status_priority === 'override') {
      //   finalMessage += " Status manually overridden.";
      // } else if (auto_updated) {
      //   finalMessage += " Status auto-updated.";
      // }

      setAlertContent(finalMessage);
      setAlertType("success");
      setAlert(true);

      // Invalidate React Query cache to refresh job list
      queryClient.invalidateQueries({ queryKey: ['jobs'] });

      setTimeout(() => {
        const from = location.state?.from || "";
        navigate(`/jobs${from}`);
      }, 2500);

      } catch (error) {
        console.error('Update Job Error:', error?.response?.data);
        const errData = error?.response?.data || {};
        let errMsg = "Update failed";
        if (typeof errData.message === 'string') errMsg = errData.message;
        else if (typeof errData.error === 'string') errMsg = errData.error;
        else if (errData.error?.message) errMsg = errData.error.message;
        else errMsg = JSON.stringify(errData);
        
        setAlertContent(errMsg);
        setAlertType("error");
        setAlert(true);
      }
  };



  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required("Job title is required.")
      .min(2, "Minimum 2 characters are required.")
      .max(100, "Maximum length is 100 characters.")
      .test(
        "no-empty-or-spaces",
        "Title must not be empty or contain only spaces.",
        (value) => value?.trim().length > 0
      )
      .test(
        "no-leading-trailing-spaces",
        "Leading and trailing spaces are not allowed.",
        (value) => value === value?.trim()
      )
      .matches(
        /^[a-zA-Z0-9.,#\-_ ]+$/,
        "Please use only letters, numbers, spaces, and symbols: . , # - _"
      ),
    location: Yup.string()
      .nullable().notRequired()
      .test('valid-locations', 'Invalid location format', function (value) {
        if (!value?.trim()) return true;
        const locationArray = value.split(',').map(loc => loc.trim()).filter(loc => loc.length > 0);
        const locationRegex = /^[a-zA-Za-z\s]+$/;
        const isValid = locationArray.every(loc =>
          loc.length >= 2 &&
          loc.length <= 50 &&
          locationRegex.test(loc)
        );
        return isValid && locationArray.length > 0;
      }),
    experience: Yup.string()
      .min(1, "Minimum 1 caharacter is required.")
      .max(20, "Maximum length is 20 characters.")
      .nullable().notRequired(),
    currency: Yup.string().nullable().notRequired(),
    budget: Yup.string().nullable().notRequired(),
    budget_uom: Yup.string().nullable().notRequired(),
    mode_of_work: Yup.string().nullable().notRequired(),
    notice_period: Yup.string()
      .required('Timeline (Days) is required.')
      .matches(/^\d+$/, 'Must be numbers only (days)')
      .test('timeline-range', 'Timeline must be between 1 and 365 days.', (value) => !value || (Number(value) >= 1 && Number(value) <= 365)),
    // job_description: Yup.string()
    //   .min(2, "Minimum 2 caharacter is required.")
    //   .max(100, "Maximum length is 100 characters.")
    //   .nullable().notRequired(),
    status: Yup.string().nullable().notRequired(),
    // notes: Yup.string()
    //   .min(2, "Minimum 2 caharacter is required.")
    //   .max(255, "Maximum length is 255 characters.")
    //   .notRequired(),

    notes: Yup.string()
      .max(255, "Maximum length is 255 characters.")
      .when("status", {
        is: (status) => NOTES_REQUIRED_STATUSES.includes(status),
        then: (schema) =>
          schema
            .trim()
            .required("Notes are required for this status.")
            .min(2, "Minimum 2 characters are required."),
        otherwise: (schema) => schema.notRequired(),
      }),
    // .nullable().notRequired(),
    // primary_skills: Yup.array().nullable().notRequired(),
    coreSkillId: Yup.number()
      .typeError("This field is required.")
      .nullable().notRequired(),
    techSkills: Yup.array().nullable().notRequired(),
    client_id: Yup.number()
      .typeError("This field is required")
      .required("Client is required"),
    contact_id: Yup.number()
      .nullable()
      .when('client_id', {
        is: (client_id) => !!client_id,
        then: (schema) => schema.nullable().notRequired(),
        otherwise: (schema) => schema.notRequired()
      }),
    no_of_requirements: Yup.number()
      .typeError("Numbers of Requirements is required.")
      .required("Numbers of Requirements is required.")
      .min(1, "Minimum 1 requirement"),
    contact_person_name: Yup.string().max(50, "Max 50 characters"),
    contact_person_number: Yup.string()
      .matches(/^[\+]?[1-9][\d]{0,15}$/, "Invalid phone number"),
    contact_person_email: Yup.string().email("Invalid email"),
    fulfilled_count: Yup.number()
      .typeError("Filled Positions is required.")
      .required("Filled Positions is required.")
      .min(0, "Cannot be negative")
      .when("status", {
        is: (val) => val === "Partially-Fulfilled",
        then: (schema) =>
          schema.test(
            "partial-range",
            "Filled positions is out of range for Partially Filled status",
            function (value) {
              const { no_of_requirements } = this.parent;
              if (!no_of_requirements || value == null) return true;
              if (no_of_requirements < 2) return this.createError({ message: "Partially-Fulfilled needs at least 2 requirements" });
              if (value < 1) return this.createError({ message: "Filled positions must be at least 1 for Partially Filled status" });
              if (value >= no_of_requirements) return this.createError({ message: "Filled positions must be less than total requirements for Partially Filled status" });
              return true;
            }
          ),
        otherwise: (schema) => schema,
      })
      .when("status", {
        is: (val) => val === "Full-Filled",
        then: (schema) =>
          schema
            .test(
              "equals-req",
              "Filled positions must equal total requirements for Full Filled status",
              function (value) {
                const { no_of_requirements } = this.parent;
                if (!no_of_requirements || value == null) return true;
                return value >= no_of_requirements; // Allow equal or greater (over-hired)
              }
            ),
        otherwise: (schema) => schema,
      }),
    background_verification: Yup.boolean().nullable().notRequired(),
    pipeline_type: Yup.string()
      .oneOf(['resume_based', 'non_resume_based'], 'Please select a valid job type')
      .required('Job type is required'),
    assignment_duration_days: Yup.number()
      .min(1, 'Minimum 1 day')
      .max(3650, 'Maximum 10 years (3650 days)')
      .nullable().notRequired(),
  });

  const getSkills = async () => {
    let url = `/api/skill/skill_list`;

    try {
      const res = await API.get(url, config);
      const formattedSkills = res?.data?.skill?.map((skill) => ({
        id: skill.skill_id,
        value: skill.skill_name,
        label: skill.skill_name,
      }));
      setSkillData(formattedSkills);
    } catch (error) {
      console.log("error", error);
    }
  };
  const [coreSkills, setCoreSkills] = useState([]);
  const othersSkill = coreSkills.find(s => s.label.trim().toLowerCase() === 'others') ?? null;
  const [techSkills, setTechSkills] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [locations, setLocations] = useState([]);

  const getLocations = () => {
    try {
      let locationArray = [];

      if (!jobDetails?.location) {
        setLocations([]);
        return;
      }

      const locationValue = jobDetails.location.trim();

      // Case 1: Single plain string "Pune"
      if (locationValue && !locationValue.startsWith('[') && !locationValue.includes(',')) {
        locationArray = [locationValue];
      }
      // Case 2: JSON array string ["Pune","Mumbai"] or "[\"Pune\",\"Mumbai\"]"
      else if (locationValue.startsWith('[')) {
        locationArray = JSON.parse(locationValue);
      }
      // Case 3: Single escaped JSON string "\"Pune\""
      else if (locationValue.startsWith('"') && locationValue.endsWith('"')) {
        const unescaped = JSON.parse(locationValue); // "\"Pune\"" → "Pune"
        locationArray = [unescaped];
      }
      // Case 4: Already array (fallback)
      else if (Array.isArray(jobDetails.location)) {
        locationArray = jobDetails.location;
      }

      // Format for state
      const formattedLocations = Array.isArray(locationArray)
        ? locationArray.map((loc, index) => ({
          id: index,
          value: loc,
          label: loc
        }))
        : [];

      setLocations(formattedLocations);

      // Update formik initial value for display
      const displayValue = Array.isArray(locationArray)
        ? locationArray.join(', ')
        : locationArray.toString();

      // Note: Formik initialValues handles display separately

    } catch (error) {
      console.log('Location parse error:', error);
      console.log('Raw location:', jobDetails?.location);
      setLocations([]);
    }
  };


  const getCoreSkills = async () => {
    try {
      const res = await API.get("/api/core-skill/coreSkills", config);
      const formattedCore = res?.data?.coreSkills?.map(item => ({
        id: item.id,
        value: item.id,
        label: item.name,
      }));
      setCoreSkills(formattedCore);
    } catch (error) {
      console.log("getCoreSkills error", error);
    }
  };

  // const getTechSkillsByCore = async (coreSkillId) => {
  //   if (!coreSkillId) {
  //     setTechSkills([]);
  //     return;
  //   }
  //   try {
  //     const res = await API.get(`/api/tech-skill/alltechSkills/${coreSkillId}`, config);
  //     const formattedTech = res?.data?.techSkills?.map(item => ({
  //       id: item.id,
  //       value: item.name,  // Use name as value for techSkills array of strings
  //       label: item.name,
  //     }));
  //     setTechSkills(formattedTech);
  //   } catch (error) {
  //     console.log("getTechSkillsByCore error", error);
  //   }
  // };

  const getTechSkillsByCore = async (coreSkillId) => {
    if (!coreSkillId) {
      setTechSkills([]);
      return;
    }
    try {
      const res = await API.get(`/api/tech-skill/alltechSkills/${coreSkillId}`, config);
      const formattedTech = res?.data?.techSkills?.map(item => ({
        id: item.id,
        value: item.name,
        label: item.name,
      }));

      // ADD "All Skills" option (NEW)
      const allSkillsOption = { id: 'all', value: 'All Skills', label: 'All Skills' };
      setTechSkills([allSkillsOption, ...formattedTech]); // ← CHANGED
    } catch (error) {
      console.log("getTechSkillsByCore error", error);
    }
  };

  // Add this function before the main return (
  const handleTechSkillsChange = (event, form) => {
    const selectedValues = event.target.value;

    if (selectedValues.includes('All Skills')) {
      // Select ALL available tech skills (exclude "All Skills" itself)
      const allTechSkills = techSkills
        .filter(skill => skill.value !== 'All Skills')
        .map(skill => skill.value);
      form.setFieldValue('techSkills', allTechSkills);
    } else {
      form.setFieldValue('techSkills', selectedValues);
    }
  };

  const getClients = async () => {
    try {
      const res = await API.get('/api/client/active_client_list', config);
      const formattedClients = res?.data?.clients?.map(item => ({
        id: item.client_id,
        value: item.client_id,
        label: `${item.name}`,
        client_code: item.client_code,
        name: item.name
      }));
      setClients(formattedClients);
    } catch (error) {
      console.log('getClients error:', error);
    }
  };
  const getClientContacts = useCallback(async (clientId) => {
    if (!clientId) {
      setClientContacts([]);
      return;
    }
    try {
      const res = await API.get(`/api/client/ClientsContacts/${clientId}`, config);
      const formattedContacts = res?.data?.contacts?.map(item => ({
        id: item.contact_id,
        value: item.contact_id,
        label: `${item.name}`,
        name: item.name,
        email: item.email,
        phone: item.phone
      }));
      setClientContacts(formattedContacts);
    } catch (error) {
      console.log('getClientContacts error', error);
    }
  }, [config]);



  const getSavedScoringPrompts = async () => {
    try {
      const res = await API.get("/api/job/scoring-prompts", config);
      setSavedPrompts(res?.data?.prompts || []);
    } catch (error) {
      console.log("getSavedScoringPrompts error", error);
    }
  };

  useEffect(() => {
    getSkills();
    getCoreSkills();
    getClients();
    getLocations();
    getSavedScoringPrompts();
    if (initialValues.coreSkillId) {
      getTechSkillsByCore(initialValues.coreSkillId);
    }
    if (jobDetails?.client_id) {
      getClientContacts(jobDetails.client_id); // Load contacts for existing job
    }
  }, []);

  const navigateToPreviousPage = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleClientChange = useCallback((value, form) => {
    form.setFieldValue('client_id', value);
    getClientContacts(value); // Fetch contacts
    form.setFieldValue('contact_id', ''); // Reset contact
  }, [getClientContacts]);

  const handleContactChange = useCallback((value, form) => {
    form.setFieldValue('contact_id', value);
  }, []);

  const handleLocationChange = useCallback((value, form) => {
    form.setFieldValue('location', value);
  }, []);

  return (
    <div style={containerStyle}>
      <Button startIcon={<ArrowBackIcon />} onClick={navigateToPreviousPage}>
        Back
      </Button>
      <div
        style={formContainerStyle}
      >
        <div
          style={headerStyle}
        >
          <span>Edit Job</span>
          <div style={gridContainerStyle}>
            <Formik
              initialValues={initialValues}
              onSubmit={onSubmit}
              validationSchema={validationSchema}
            >
              {(props) => (
                <Form noValidate>
                  {console.log("Formik State: ", { isValid: props.isValid, dirty: props.dirty, errors: props.errors })}
                  <Grid container spacing={2}>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Job Title <span style={{ color: 'red' }}>*</span></span>
                      <Field
                        as={TextField}
                        name="title"
                        fullWidth
                        error={props.errors.title && props.touched.title}
                        size="small"
                        helperText={<ErrorMessage name="title" />}
                        required
                      />{" "}
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={labelStyle}>Client <span style={requiredStyle}>*</span></span>
                      <Field name="client_id">
                        {({ field, form }) => (
                          <FormControl fullWidth variant="outlined" size="small" error={form.touched.client_id && Boolean(form.errors.client_id)}>
                            <Select
                              {...field}
                              onChange={(event) => handleClientChange(event.target.value, form)}
                              displayEmpty
                            >
                              {clients.map((option) => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: 'red' }}>
                              <ErrorMessage name="client_id" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={labelStyle}>Contact Person</span>
                      <Field name="contact_id">
                        {({ field, form }) => (
                          <FormControl fullWidth variant="outlined" size="small" error={form.touched.contact_id && Boolean(form.errors.contact_id)}>
                            <Select
                              {...field}
                              value={field.value || ''}
                              onChange={(event) => handleContactChange(event.target.value, form)}
                              displayEmpty
                            >
                              {clientContacts.map(option => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: 'red' }}>
                              <ErrorMessage name="contact_id" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    {/* Job Type */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={labelStyle}>Job Type <span style={requiredStyle}>*</span></span>
                      <Field name="pipeline_type">
                        {({ field, form }) => (
                          <FormControl fullWidth variant="outlined" size="small" error={form.touched.pipeline_type && Boolean(form.errors.pipeline_type)}>
                            <Select
                              {...field}
                              value={field.value || ''}
                              onChange={(e) => {
                                const value = e.target.value;
                                form.setFieldValue('pipeline_type', value);
                                if (value === 'non_resume_based') {
                                  form.setFieldValue('coreSkillId', othersSkill ? othersSkill.value : '');
                                  form.setFieldValue('techSkills', []);
                                } else {
                                  form.setFieldValue('coreSkillId', '');
                                  form.setFieldValue('techSkills', []);
                                }
                              }}
                              displayEmpty
                            >
                              <MenuItem value="" disabled><em>Select Job Type</em></MenuItem>
                              <MenuItem value="resume_based">White Collar (Resume Required)</MenuItem>
                              <MenuItem value="non_resume_based">Blue Collar (No Resume)</MenuItem>
                            </Select>
                            <FormHelperText sx={{ color: 'red' }}>
                              <ErrorMessage name="pipeline_type" />
                            </FormHelperText>
                            {props.values.pipeline_type === 'non_resume_based' && !othersSkill && (
                              <FormHelperText sx={{ color: '#ed6c02' }}>
                                "Others" skill not found in system. Please select a Core Skill manually.
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={labelStyle}>
                        Locations
                      </span>
                      <Field name="location">
                        {({ field, form, meta }) => (
                          <TextField
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => handleLocationChange(e.target.value, form)}
                            fullWidth
                            size="small"
                            placeholder="Pune, Mumbai, Bangalore"
                            error={meta.touched && Boolean(meta.error)}
                            helperText={
                              meta.touched && meta.error
                                ? <ErrorMessage name="location" />
                                : 'Enter multiple locations separated by commas'
                            }
                          />
                        )}
                      </Field>

                    </Grid>




                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Experience </span>
                      <FIelds name="experience" props={props} onInput="yes" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Currency </span>
                      <CustomSelect
                        name="currency"
                        props={props}
                        options={allCurrency}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Budget </span>
                      <FIelds name="budget" props={props} onInput="yes" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Budget UOM </span>
                      <CustomSelect
                        name="budget_uom"
                        props={props}
                        options={budgetUOM}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Work Mode </span>
                      <CustomSelect
                        name="mode_of_work"
                        props={props}
                        options={WorkMode}
                      />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>
                        Timeline (Days) <span style={{ color: 'red' }}>*</span>
                      </span>
                      <FIelds
                        name="notice_period"
                        props={props}
                        onInput="yes"
                      />
                    </Grid>
                    {/* Assignment Duration - Add after Notice Period, before Core Skill */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Assignment Duration (Days) </span>
                      <Field
                        as={TextField}
                        name="assignment_duration_days"
                        type="number"
                        fullWidth
                        error={props.errors.assignment_duration_days && props.touched.assignment_duration_days}
                        size="small"
                        helperText={<ErrorMessage name="assignment_duration_days" />}
                        inputProps={{ min: 1, max: 3650 }}
                        placeholder="90"
                        required
                      />
                    </Grid>

                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Primary Skills</span>
                      <CustomMultiSelect
                        name="primary_skills"
                        props={props}
                        options={skillData}
                      />
                    </Grid> */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Core Skill </span>
                      <Field name="coreSkillId">
                        {({ field, form }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={form.touched.coreSkillId && Boolean(form.errors.coreSkillId)}
                          >
                            <Select
                              {...field}
                              disabled={props.values.pipeline_type === 'non_resume_based' && !!othersSkill}
                              displayEmpty
                              onChange={(event) => {
                                const value = event.target.value;
                                form.setFieldValue("coreSkillId", value);
                                form.setFieldValue("techSkills", []); // reset tech skills on core skill change
                                getTechSkillsByCore(value);
                              }}
                              size="small"
                              variant="outlined"
                            >
                              {coreSkills.map(option => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: "red" }}>
                              <ErrorMessage name="coreSkillId" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Tech Skills</span>
                      <Field name="techSkills">
                        {({ field, form }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={form.touched.techSkills && Boolean(form.errors.techSkills)}
                          >
                            <Select
                              multiple
                              displayEmpty
                              MenuProps={{ disablePortal: true }}
                              {...field}
                              value={field.value || []}
                              onChange={event => {
                                form.setFieldValue("techSkills", event.target.value);
                              }}
                              onBlur={field.onBlur}
                              size="small"
                              variant="outlined"
                            >
                              {techSkills.map(option => (
                                <MenuItem key={option.id} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText sx={{ color: "red" }}>
                              <ErrorMessage name="techSkills" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid> */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Tech Skills </span>
                      <Field name="techSkills">
                        {({ field, form }) => (
                          <FormControl
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={form.touched.techSkills && Boolean(form.errors.techSkills)}
                          >
                            <Select
                              multiple
                              displayEmpty
                              MenuProps={{ disablePortal: true }}
                              {...field}
                              disabled={props.values.pipeline_type === 'non_resume_based'}
                              value={field.value || []}
                              onChange={(event) => handleTechSkillsChange(event, form)}
                              onBlur={field.onBlur}
                              size="small"
                              variant="outlined"
                              renderValue={(selected) => (
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 6,
                                    maxHeight: 60,        // ✅ Prevents stretching
                                    overflowY: "auto"     // ✅ Scroll instead of grow
                                  }}
                                >
                                  {selected.map((value) => (
                                    <span
                                      key={value}
                                      style={{
                                        background: "#E3F2FD",
                                        padding: "4px 8px",
                                        borderRadius: 12,
                                        fontSize: "13px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {value}
                                    </span>
                                  ))}
                                </div>
                              )}
                            >
                              {/* "All Skills" option - NEW */}
                              <MenuItem value="All Skills">All Skills</MenuItem>
                              {/* Regular tech skills (exclude "All Skills" from selectable list) */}
                              {techSkills
                                .filter(option => option.value !== 'All Skills')
                                .map(option => (
                                  <MenuItem key={option.id} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText sx={{ color: "red" }}>
                              <ErrorMessage name="techSkills" />
                            </FormHelperText>
                          </FormControl>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Status </span>
                      <CustomSelect
                        name="status"
                        props={props}
                        options={allStatus}
                      />
                      {NOTES_REQUIRED_STATUSES.includes(props.values.status) && (
                        <div
                          style={{
                            marginTop: '6px',
                            padding: '7px 12px',
                            background: '#fff8e1',
                            border: '1px solid #ffe082',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            color: '#b45309',
                            fontWeight: 500,
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>⚠️</span>
                          <span>
                            <strong>Notes is mandatory</strong> for{' '}
                            <em>Hold / Cancelled / Closed</em> status.
                          </span>
                        </div>
                      )}
                      {props.values.status === 'Full-Filled' && (
                        <div
                          style={{
                            marginTop: '6px',
                            padding: '7px 12px',
                            background: '#e8f5e9',
                            border: '1px solid #a5d6a7',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            color: '#1b5e20',
                            fontWeight: 500,
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>ℹ️</span>
                          <span>
                            <strong>Filled Positions is required</strong> and must equal{' '}
                            <strong>No. of Requirements ({props.values.no_of_requirements})</strong> for Full-Filled status.
                          </span>
                        </div>
                      )}
                      {props.values.status === 'Partially-Fulfilled' && (
                        <div
                          style={{
                            marginTop: '6px',
                            padding: '7px 12px',
                            background: '#e3f2fd',
                            border: '1px solid #90caf9',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            fontSize: '13px',
                            color: '#0d47a1',
                            fontWeight: 500,
                          }}
                        >
                          <span style={{ fontSize: '16px' }}>ℹ️</span>
                          {(props.values.no_of_requirements || 1) - 1 < 1 ? (
                            <span>
                              <strong>Partially-Fulfilled needs at least 2 requirements.</strong> Use Active or Full-Filled.
                            </span>
                          ) : (
                            <span>
                              <strong>Filled Positions is required</strong> — enter a value between{' '}
                              <strong>1</strong> and{' '}
                              <strong>{(props.values.no_of_requirements || 1) - 1}</strong> for Partially-Fulfilled status.
                            </span>
                          )}
                        </div>
                      )}
                    </Grid>
                    {/* BGV CHECKBOX - Add after Work Mode Grid, before Notice Period */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>BGV</span>
                      <Field name="background_verification">
                        {({ field, form, meta }) => (
                          <FormControl fullWidth error={meta.touched && Boolean(meta.error)} size="small">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="checkbox"
                                checked={field.value || false}
                                onChange={(e) => form.setFieldValue('background_verification', e.target.checked)}
                                style={{ width: '18px', height: '18px', accentColor: '#2777AA' }}
                              />
                              <span style={{ fontSize: '16px', fontWeight: '500' }}>
                                Background Verification
                              </span>
                            </div>
                            {meta.touched && meta.error && (
                              <FormHelperText sx={{ color: 'red', marginLeft: '26px' }}>
                                {meta.error}
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}
                      </Field>
                    </Grid>

                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Numbers of Requirements <span style={{ color: 'red' }}>*</span></span>
                      <Field as={TextField}
                        name="no_of_requirements"
                        type="number"
                        fullWidth
                        error={props.errors.no_of_requirements && props.touched.no_of_requirements}
                        size="small"
                        helperText={<ErrorMessage name="no_of_requirements" />}
                        required
                        inputProps={{ min: 1, max: 100 }}
                      />
                    </Grid>
                    {/* ✅ NEW: Filled Positions field */}
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>
                        Filled Positions{' '}
                        <span style={{ color: 'red' }}>*</span>
                        {props.values.status === 'Full-Filled' && (
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#1b5e20', fontStyle: 'italic', fontWeight: 400 }}>
                            (must equal {props.values.no_of_requirements})
                          </span>
                        )}
                        {props.values.status === 'Partially-Fulfilled' && (
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#0d47a1', fontStyle: 'italic', fontWeight: 400 }}>
                            (must be 1 – {(props.values.no_of_requirements || 1) - 1})
                          </span>
                        )}
                      </span>
                      <Field
                        as={TextField}
                        name="fulfilled_count"
                        type="number"
                        fullWidth
                        error={props.errors.fulfilled_count && props.touched.fulfilled_count}
                        size="small"
                        helperText={
                          props.errors.fulfilled_count && props.touched.fulfilled_count
                            ? <ErrorMessage name="fulfilled_count" />
                            : props.values.status === 'Full-Filled'
                            ? `Enter ${props.values.no_of_requirements} to match total requirements`
                            : props.values.status === 'Partially-Fulfilled'
                            ? ((props.values.no_of_requirements || 1) - 1 < 1
                              ? 'Partially-Fulfilled needs at least 2 requirements'
                              : `Enter a value between 1 and ${(props.values.no_of_requirements || 1) - 1}`)
                            : ''
                        }
                        inputProps={{ min: 0, max: 100 }}
                      />
                    </Grid>
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}></Grid> */}
                    {/* <Grid item xl={4} lg={4} md={6} sm={12} xs={12}></Grid> */}
                    <Grid item xl={8} lg={8} md={8} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>
                        Notes{' '}
                        {NOTES_REQUIRED_STATUSES.includes(props.values.status) && <span style={{ color: 'red' }}>*</span>}
                        {NOTES_REQUIRED_STATUSES.includes(props.values.status) && (
                          <span
                            style={{
                              marginLeft: '8px',
                              fontSize: '12px',
                              color: '#b45309',
                              fontWeight: 400,
                              fontStyle: 'italic',
                            }}
                          >
                            (required for this status)
                          </span>
                        )}
                      </span>
                      <TextArea name="notes" props={props} />
                    </Grid>
                    {/* Voice Bot Configuration */}
                    <Grid item xs={12}>
                      <div style={{ borderTop: '1px solid #e0e0e0', margin: '8px 0 4px 0' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Voice Bot Configuration
                      </span>
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Voice Bot ID</span>
                      <Field as={TextField} name="voice_bot_id" fullWidth size="small" placeholder="e.g. 21" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Campaign Name</span>
                      <Field as={TextField} name="campaign_name" fullWidth size="small" placeholder="e.g. shyena_hiring" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>DB Customer Name</span>
                      <Field as={TextField} name="db_customer_name" fullWidth size="small" placeholder="e.g. shyena_hiring_agent" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Processes</span>
                      <Field as={TextField} name="processes" fullWidth size="small" placeholder="e.g. Intern Hiring" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Organisation Name</span>
                      <Field as={TextField} name="organisation_name" fullWidth size="small" placeholder="e.g. Shyena Tech Yarns" />
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Customer Name</span>
                      <Field as={TextField} name="customer_name" fullWidth size="small" placeholder="e.g. Acme Corp" />
                    </Grid>
                    {/* Training Simulation Configuration */}
                    <Grid item xs={12}>
                      <div style={{ borderTop: '1px solid #e0e0e0', margin: '8px 0 4px 0' }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Training Simulation Configuration
                      </span>
                    </Grid>
                    <Grid item xl={4} lg={4} md={6} sm={12} xs={12}>
                      <span style={{ fontSize: '16px' }}>Module Name</span>
                      <Field as={TextField} name="module_name" fullWidth size="small" placeholder="e.g. Intern Training Module" />
                    </Grid>
                    {/* AI Job Description Assistant UI Panel */}
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div style={{
                        background: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "10px",
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                          <span style={{ fontSize: "16px", fontWeight: "600", color: "#1E293B" }}>
                            ✨ AI Job Description Assistant
                          </span>
                          <span style={{
                            fontSize: "11px",
                            background: "#EFF6FF",
                            color: "#2563EB",
                            padding: "2px 8px",
                            borderRadius: "12px",
                            fontWeight: "600"
                          }}>
                            {/* Powered by Gemini */}
                          </span>
                        </div>
                        
                        <p style={{ fontSize: "13px", color: "#64748B", margin: "0 0 16px 0", lineHeight: "1.5" }}>
                          Type customized keywords (e.g. Python, Django, Senior) or click <strong>Pre-fill</strong> to fetch from current form inputs to generate a professional HTML job description.
                        </p>

                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} md={8}>
                            <TextField
                              placeholder="Keywords (e.g., Python, AWS, 5 years experience, Remote)"
                              size="small"
                              fullWidth
                              value={aiKeywords}
                              onChange={(e) => setAiKeywords(e.target.value)}
                              sx={{ backgroundColor: "#FFFFFF" }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4} style={{ display: "flex", gap: "10px" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handlePrefillKeywords(props.values)}
                              sx={{
                                borderColor: "#2777AA",
                                color: "#2777AA",
                                textTransform: "none",
                                height: "38px",
                                whiteSpace: "nowrap",
                                flexGrow: 1
                              }}
                            >
                              Pre-fill
                            </Button>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleGenerateAIDescription(props.values, props.setFieldValue)}
                              disabled={isGenerating}
                              sx={{
                                background: "#2777AA",
                                "&:hover": { background: "#1B5D88" },
                                textTransform: "none",
                                height: "38px",
                                whiteSpace: "nowrap",
                                flexGrow: 1
                              }}
                            >
                              {isGenerating ? "Generating..." : "Generate AI"}
                            </Button>
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>

                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <span style={{ fontSize: "16px" }}>Job Description <span style={{ color: 'red' }}>*</span></span>
                      <TextEditor
                        getContent={(content) => {
                          handleContentChange(content);
                          props.setFieldValue("job_description", content);
                        }}
                        setClearData={setClearData}
                        clearData={clearData}
                        initialContent={aiGeneratedContent || jobDetails.job_description}
                        onEditorEmptyChange={handleEditorEmptyChange}
                      />
                      {isFormSubmitted && editorIsEmpty && (
                        <div
                          style={{
                            color: "#d32f2f",
                            marginLeft: "14px",
                            fontWeight: 400,
                            fontSize: "0.75rem",
                          }}
                        >
                          This Field is required
                        </div>
                      )}
                    </Grid>

                    {/* Additive Resume-Scoring Instructions — appended after the RAG
                        service's own base scoring prompt, never a replacement for it.
                        Leaving this blank means the job scores exactly as it always has. */}
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div style={{
                        background: "#F8FAFC",
                        border: "1px solid #E2E8F0",
                        borderRadius: "12px",
                        padding: "20px",
                        marginBottom: "10px",
                        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.05)"
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
                          <span style={{ fontSize: "16px", fontWeight: "600", color: "#1E293B" }}>
                            🎯 Customize The Prompt 
                          </span>
                        </div>

                        <p style={{ fontSize: "13px", color: "#64748B", margin: "0 0 16px 0", lineHeight: "1.5" }}>
                          This job automatically inherits the AI scoring prompt configured for this Client/Organization in <strong>Prompt Management</strong>.
                          Optionally pick or write custom role-specific notes below to append on top, or clear this box to score using standard rules.
                        </p>

                        <Grid container spacing={2} alignItems="center">
                          {/* Two dropdowns, same saved-prompts list, different label to search
                              by. Both are driven by the same selectedSavedPromptId, so picking
                              one automatically highlights the matching entry in the other. */}
                          <Grid item xs={12} md={6}>
                            <span style={{ fontSize: "13px", color: "#64748B" }}>Reuse by Prompt Name</span>
                            <FormControl fullWidth variant="outlined" size="small">
                              <Select
                                value={selectedSavedPromptId}
                                displayEmpty
                                onChange={(e) => handleSelectSavedPrompt(e.target.value, props)}
                                sx={{ backgroundColor: "#FFFFFF" }}
                              >
                                <MenuItem value="">
                                  <em>Select by prompt name…</em>
                                </MenuItem>
                                {savedPrompts.map((p) => (
                                  <MenuItem key={p.scoring_prompt_id} value={p.scoring_prompt_id}>
                                    {p.prompt_name || p.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <span style={{ fontSize: "13px", color: "#64748B" }}>Reuse by Job Title</span>
                            <FormControl fullWidth variant="outlined" size="small">
                              <Select
                                value={selectedSavedPromptId}
                                displayEmpty
                                onChange={(e) => handleSelectSavedPrompt(e.target.value, props)}
                                sx={{ backgroundColor: "#FFFFFF" }}
                              >
                                <MenuItem value="">
                                  <em>Select by job title…</em>
                                </MenuItem>
                                {savedPrompts.map((p) => (
                                  <MenuItem key={p.scoring_prompt_id} value={p.scoring_prompt_id}>
                                    {p.title}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <span style={{ fontSize: "13px", color: "#64748B" }}>Prompt Name</span>
                            <TextField
                              placeholder="e.g. Python Developer — Python + Azure"
                              fullWidth
                              size="small"
                              value={promptName}
                              onChange={(e) => handlePromptNameChange(e.target.value, props)}
                              sx={{ backgroundColor: "#FFFFFF" }}
                            />
                          </Grid>
                          <Grid item xs={12} md={4} style={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleGenerateScoringPrompt(props.values)}
                              disabled={isGeneratingPrompt}
                              sx={{
                                borderColor: "#2777AA",
                                color: "#2777AA",
                                textTransform: "none",
                                height: "38px",
                                whiteSpace: "nowrap",
                                marginTop: "20px",
                              }}
                            >
                              {isGeneratingPrompt ? "Generating..." : "Generate from Job Details"}
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              placeholder="e.g. Prioritize AWS certification as a must-have; weight leadership experience heavily for this senior role…"
                              multiline
                              minRows={3}
                              fullWidth
                              size="small"
                              value={scoringPromptText}
                              onChange={(e) => handleScoringPromptTextChange(e.target.value, props)}
                              sx={{ backgroundColor: "#FFFFFF" }}
                            />
                          </Grid>
                        </Grid>
                      </div>
                    </Grid>

                    {/* Submit */}
                    <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {props.values.needs_to_review && (
                          <Button 
                            variant="outlined"
                            color="success" 
                            title="Mark as Reviewed" 
                            onClick={async () => {
                              try {
                                await API.put(`/api/job/${jobDetails.job_id}`, { needs_to_review: false }, config);
                                setAlertContent("Job marked as reviewed successfully!");
                                setAlertType("success");
                                setAlert(true);
                                props.setFieldValue("needs_to_review", false);
                                // Invalidate jobs list cache so the star disappears immediately when navigating back
                                queryClient.invalidateQueries({ queryKey: ['jobs'] });
                              } catch (error) {
                                setAlertContent(error?.response?.data?.message || 'Failed to update job review status');
                                setAlertType("error");
                                setAlert(true);
                              }
                            }}
                            sx={{ 
                              mr: 2,
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "16px",
                              fontWeight: "500",
                              letterSpacing: 1,
                            }}
                            startIcon={<CheckCircleIcon />}
                          >
                            Mark as Reviewed
                          </Button>
                        )}
                        
                        {/* Recruiter: Secure Open Source Email Button */}
                        {(isRecruiter || role?.toLowerCase() === "recruiter") && jobDetails.source_email_link && (
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => window.open(jobDetails.source_email_link, "_blank")}
                            sx={{
                              mr: 2,
                              fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                              fontSize: "16px",
                              fontWeight: "500",
                              letterSpacing: 1,
                            }}
                          >
                            Open Source Email
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          aria-haspopup="true"
                          sx={{
                            backgroundColor: "#2777AA",
                            color: "#fff",
                            fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                            fontSize: "16px",
                            fontWeight: "500",
                            letterSpacing: 1,
                          }}
                          type="submit"
                        >
                          Update
                        </Button>
                      </div>
                    </Grid>
                  </Grid>
                  <Notification
                    message={alertContent}
                    open={alert}
                    severity={alertType}
                    onClose={handleAlert}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditJob;

const WorkMode = [
  { id: 1, value: "remote", label: "Work Remotely" },
  { id: 2, value: "office", label: "Work From Office" },
  { id: 3, value: "hybrid", label: "Hybrid" },
];
const allStatus = [
  { id: 1, value: "Active", label: "Active" },
  { id: 2, value: "Full-Filled", label: "Full-Filled" },
  { id: 3, value: "Critical", label: "Critical" },
  { id: 4, value: "Hold", label: "Hold" },
  { id: 5, value: "Cancelled", label: "Cancelled" },
  { id: 6, value: "Partially-Fulfilled", label: "Partially-Fulfilled" },
  { id: 7, value: "Close-No_Client_Response", label: "Close-No Client Response" },
  { id: 8, value: "Close-Unable_To_Fulfill", label: "Close-Unable To Fulfill" },
];
const allCurrency = [
  { id: 1, value: "INR", label: "INR" },
  { id: 2, value: "USD", label: "USD" },
  { id: 3, value: "GBP", label: "GBP" },
];
const budgetUOM = [
  { id: 1, value: "perHour", label: "Per Hour" },
  { id: 2, value: "perMonth", label: "Per Month" },
  { id: 3, value: "perDay", label: "Per Day" },
];